"use client";

import { useMemo } from "react";
import { FgInput } from "@/components/fg-input";
import { FgToggle } from "@/components/fg-toggle";
import { MediaUploader } from "../MediaUploader";
import { FormValidityBanner } from "../FormValidityBanner";
import { RubricEditor, type RubricCriterion } from "../RubricEditor";
import { MobilePreview } from "../MobilePreview";

export interface FreeAnswerBuilderProps {
  /** Prompt kind — text or audio. */
  promptKind: "text" | "audio";
  onPromptKindChange: (next: "text" | "audio") => void;
  /** Prompt text (always present). */
  promptText: string;
  onPromptTextChange: (next: string) => void;
  /** Optional audio URL for the prompt (only when kind === 'audio'). */
  promptAudioUrl: string;
  onPromptAudioUrlChange: (next: string) => void;
  /** Canonical reference answer — used by Gemini as guidance. */
  referenceAnswer: string;
  onReferenceAnswerChange: (next: string) => void;
  /** Max answer length in chars. */
  maxLength: number;
  onMaxLengthChange: (next: number) => void;
  /** Difficulty 1-5. */
  difficulty: number;
  onDifficultyChange: (next: number) => void;
  /** AI grading on/off. */
  aiGradingEnabled: boolean;
  onAiGradingEnabledChange: (next: boolean) => void;
  rubric: RubricCriterion[];
  onRubricChange: (next: RubricCriterion[]) => void;
  explanation?: string;
  onExplanationChange?: (next: string) => void;
  level: "A1" | "A2" | "B1" | "B2" | "C1";
}

export function FreeAnswerBuilder({
  promptKind,
  onPromptKindChange,
  promptText,
  onPromptTextChange,
  promptAudioUrl,
  onPromptAudioUrlChange,
  referenceAnswer,
  onReferenceAnswerChange,
  maxLength,
  onMaxLengthChange,
  difficulty,
  onDifficultyChange,
  aiGradingEnabled,
  onAiGradingEnabledChange,
  rubric,
  onRubricChange,
  explanation,
  onExplanationChange,
  level,
}: FreeAnswerBuilderProps) {
  const validity = useMemo(
    () => [
      {
        label: "Prompt written",
        severity: promptText.trim() ? ("ok" as const) : ("error" as const),
      },
      {
        label: "Reference answer provided",
        severity: referenceAnswer.trim() ? ("ok" as const) : ("warn" as const),
      },
      {
        label: "Rubric weights sum to 100",
        severity:
          rubric.reduce((s, r) => s + r.weight, 0) === 100
            ? ("ok" as const)
            : ("warn" as const),
      },
    ],
    [promptText, referenceAnswer, rubric],
  );

  const promptPreview = promptText || "—";

  return (
    <div className="flex flex-col gap-4">
      <FormValidityBanner title="Free answer" checks={validity} />

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.6fr,1fr]">
        <div className="flex flex-col gap-3">
          <PromptKindPicker
            kind={promptKind}
            onKindChange={onPromptKindChange}
            text={promptText}
            onTextChange={onPromptTextChange}
            audioUrl={promptAudioUrl}
            onAudioUrlChange={onPromptAudioUrlChange}
          />
          <FgInput
            label="Reference answer"
            value={referenceAnswer}
            onChange={(e) => onReferenceAnswerChange(e.target.value)}
            placeholder="Ich lerne Deutsch."
            hint="Gemini uses this as guidance — not as exact-match."
          />
        </div>
        <MobilePreview kind="free_answer" prompt={promptPreview} />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <FgInput
          label="Max answer length (chars)"
          type="number"
          value={String(maxLength)}
          onChange={(e) => onMaxLengthChange(Number(e.target.value) || 100)}
          min={10}
          max={5000}
        />
        <DifficultyPicker value={difficulty} onChange={onDifficultyChange} />
        <FgToggle
          label="Grade with Gemini"
          checked={aiGradingEnabled}
          onChange={onAiGradingEnabledChange}
        />
      </div>

      {aiGradingEnabled && (
        <RubricEditor
          value={rubric}
          onChange={onRubricChange}
          promptHint={promptText || referenceAnswer}
          level={level}
        />
      )}

      {onExplanationChange && (
        <FgInput
          label="Explanation (optional)"
          value={explanation ?? ""}
          onChange={(e) => onExplanationChange(e.target.value)}
        />
      )}
    </div>
  );
}

/**
 * Text / Audio toggle for the prompt. Audio kind requires a URL —
 * the prompt text becomes an optional caption shown below the player.
 */
function PromptKindPicker({
  kind,
  onKindChange,
  text,
  onTextChange,
  audioUrl,
  onAudioUrlChange,
}: {
  kind: "text" | "audio";
  onKindChange: (k: "text" | "audio") => void;
  text: string;
  onTextChange: (v: string) => void;
  audioUrl: string;
  onAudioUrlChange: (v: string) => void;
}) {
  return (
    <div className="rounded-md border border-[rgba(255,255,255,0.08)] bg-[#141414] p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93]">
          Prompt
        </p>
        <div className="flex gap-1 rounded-lg bg-[#0A0A0A] p-0.5 border border-[rgba(255,255,255,0.06)]">
          {(["text", "audio"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => onKindChange(k)}
              className={
                "px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded transition-colors " +
                (kind === k
                  ? "bg-[#FFD60A] text-[#0A0A0A]"
                  : "text-[#8E8E93] hover:text-[#FAFAFA]")
              }
            >
              {k}
            </button>
          ))}
        </div>
      </div>
      {kind === "text" ? (
        <FgInput
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder='Translate: "I am learning German."'
        />
      ) : (
        <div className="space-y-2">
          <MediaUploader
            kind="audio"
            value={audioUrl}
            onChange={onAudioUrlChange}
            label="Prompt audio"
          />
          <FgInput
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            placeholder="Optional caption shown below the player"
          />
        </div>
      )}
    </div>
  );
}

function DifficultyPicker({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
        Difficulty
      </label>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => onChange(d)}
            className={
              "h-9 w-9 rounded-md border text-sm font-bold transition-colors " +
              (value >= d
                ? "bg-[#FFD60A] border-[#FFD60A] text-[#0A0A0A]"
                : "bg-[#141414] border-[rgba(255,255,255,0.08)] text-[#8E8E93] hover:text-[#FAFAFA]")
            }
            aria-label={`Difficulty ${d}`}
          >
            {d}
          </button>
        ))}
      </div>
    </div>
  );
}